import React, { useEffect, useState } from "react";
import AsteriskStyle from "../../../../../../components/Form/Tooltip/AsteriskStyle";
import AdvocateService from "../../../../../Service/AdvocateService";
import CustomSelect from "../../../../../../components/common/CustomSelect";
import ProspectPropertyService from "../../../../../Service/ProspectPropertyService";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../../components/Constant/Notification";
import { userUid } from "../../../../../../utils/storage";
import CustomButton from "../../../../../../components/common/CustomButton";
import { formatDate } from "../../../../../../utils/dateFormatter";

const AssignAdvocate = ({ propertyId, setCurrentTab, propertyData, isDropped, onDataUpdate }) => {
  const [selectedAdvocate, setSelectedAdvocate] = useState(null);
  const [docGivenDate, setDocGivenDate] = useState("");
  const [otherComments, setOtherComments] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch advocate options
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await AdvocateService.getAdvocateNames();
        const result = response.data || [];
        setData(result);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  // Immediately map propertyData to component state when data changes
  useEffect(() => {
    if (propertyData?.advocateDetails) {
      const advocateDetails = propertyData.advocateDetails;
      
      // Set selected advocate
      setSelectedAdvocate(advocateDetails?.advocate || null);
      
      // Set document given date
      setDocGivenDate(
        advocateDetails?.advocateAssignedDate 
          ? formatDate(advocateDetails.advocateAssignedDate) 
          : ""
      );
      
      // Set other comments
      setOtherComments(advocateDetails?.notes || "");
    }
  }, [propertyData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAdvocate) {
      showErrorToast("Please select an advocate.");
      return;
    }

    if (!docGivenDate) {
      showErrorToast("Please select the document given date.");
      return;
    }

    setLoading(true);
    try {
      const body = {
        propertyId: propertyId,
        advocateId: selectedAdvocate ? selectedAdvocate.value : null,
        advocateDocGivenDate: formatDate(docGivenDate),
        advocateNotes: otherComments.trim(),
        modifiedById: userUid,
      };
      const response =
        await ProspectPropertyService.postPropertyAdvocateAssignment(body);

      if (response.status === 200) {
        // Immediately refetch the updated data
        if (onDataUpdate) {
          await onDataUpdate();
        }
        setCurrentTab(2);
        showSuccessToast(
          response.data.message || "Advocate assigned successfully!"
        );
      }
    } catch (error) {
      showErrorToast(
        error.response?.data?.message ||
          "Failed to assign advocate. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 p-4">
      {/* Row: Advocate + Date */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
        {/* Select Advocate */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Select Advocate <AsteriskStyle />
          </label>
          <CustomSelect
            options={data}
            value={selectedAdvocate}
            onChange={setSelectedAdvocate}
            placeholder="Advocate Name"
            isClearable
            isDisabled={isDropped}
          />
        </div>

        {/* Doc Given Date */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Full Set of Doc given to advocate date <AsteriskStyle />
          </label>
          <input
            type="date"
            value={docGivenDate}
            onChange={(e) => setDocGivenDate(e.target.value)}
            disabled={isDropped}
            className={`w-full px-3 py-2 border rounded-md input-focus ${
              isDropped 
                ? 'border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed' 
                : 'border-gray-300'
            }`}
            placeholder="Select Date"
          />
        </div>
      </div>

      {/* Comments */}
      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-gray-700 pt-2">
          Other Comments
        </label>
        <textarea
          value={otherComments}
          rows={4}
          onChange={(e) => setOtherComments(e.target.value)}
          maxLength={500}
          disabled={isDropped}
          className={`w-full px-3 py-2 border rounded-md input-focus ${
            isDropped 
              ? 'border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed' 
              : 'border-gray-300'
          }`}
          placeholder="Type Here"
        />
      </div>

      {/* Submit button */}
      <div className="flex justify-center">
        <CustomButton
          label={loading ? "Submitting..." : "Update"}
          onClick={handleSubmit}
          isDisabled={loading || !selectedAdvocate || !docGivenDate || isDropped}
          className="w-1/2"
        />
      </div>
    </div>
  );
};

export default AssignAdvocate;
